export type AppSheet = "chat" | "marker" | "versions";

export type AppNavigation = {
  termId: string | null;
  reviewMode: boolean;
  sheet: AppSheet | null;
  blockId: string | null;
  draftId: string | null;
};

type TermClearHistoryState = {
  termclear: "navigation-v1";
  navigation: AppNavigation;
  canGoBack: boolean;
};

export function libraryNavigation(): AppNavigation {
  return { termId: null, reviewMode: false, sheet: null, blockId: null, draftId: null };
}

export function detailNavigation(termId: string, reviewMode: boolean): AppNavigation {
  return { termId, reviewMode, sheet: null, blockId: null, draftId: null };
}

export function currentNavigation(): AppNavigation {
  const state = parseHistoryState(window.history.state);
  return state?.navigation ?? navigationFromUrl(new URL(window.location.href));
}

export function currentEntryCanGoBack(): boolean {
  return parseHistoryState(window.history.state)?.canGoBack ?? false;
}

export function pushNavigation(navigation: AppNavigation): void {
  const state: TermClearHistoryState = {
    termclear: "navigation-v1",
    navigation,
    canGoBack: true,
  };
  window.history.pushState(state, "", navigationUrl(navigation));
}

export function replaceNavigation(navigation: AppNavigation): void {
  const state: TermClearHistoryState = {
    termclear: "navigation-v1",
    navigation,
    canGoBack: currentEntryCanGoBack(),
  };
  window.history.replaceState(state, "", navigationUrl(navigation));
}

function navigationFromUrl(url: URL): AppNavigation {
  const termId = url.searchParams.get("term");
  if (!termId) return libraryNavigation();

  const reviewMode = url.searchParams.get("review") === "1";
  const requestedSheet = url.searchParams.get("sheet");
  const blockId = url.searchParams.get("block");
  if ((requestedSheet === "chat" || requestedSheet === "marker") && blockId) {
    return {
      termId,
      reviewMode,
      sheet: requestedSheet,
      blockId,
      draftId: requestedSheet === "chat" ? url.searchParams.get("draft") : null,
    };
  }
  if (requestedSheet === "versions") {
    return { termId, reviewMode, sheet: "versions", blockId: null, draftId: null };
  }
  return detailNavigation(termId, reviewMode);
}

function navigationUrl(navigation: AppNavigation): string {
  const url = new URL(window.location.href);
  url.pathname = "/";
  url.search = "";
  url.hash = "";
  if (navigation.termId) {
    url.searchParams.set("term", navigation.termId);
    if (navigation.reviewMode) url.searchParams.set("review", "1");
    if (navigation.sheet) url.searchParams.set("sheet", navigation.sheet);
    if (navigation.blockId) url.searchParams.set("block", navigation.blockId);
    if (navigation.sheet === "chat" && navigation.draftId) {
      url.searchParams.set("draft", navigation.draftId);
    }
  }
  return `${url.pathname}${url.search}`;
}

function parseHistoryState(value: unknown): TermClearHistoryState | null {
  if (!isRecord(value) || value.termclear !== "navigation-v1" || typeof value.canGoBack !== "boolean") {
    return null;
  }
  const navigation = parseNavigation(value.navigation);
  return navigation ? { termclear: "navigation-v1", navigation, canGoBack: value.canGoBack } : null;
}

function parseNavigation(value: unknown): AppNavigation | null {
  if (!isRecord(value)) return null;
  const termId = typeof value.termId === "string" && value.termId ? value.termId : null;
  const reviewMode = value.reviewMode === true;
  if (!termId) return libraryNavigation();
  if (value.sheet === "versions") {
    return { termId, reviewMode, sheet: "versions", blockId: null, draftId: null };
  }
  if ((value.sheet === "chat" || value.sheet === "marker")
    && typeof value.blockId === "string" && value.blockId) {
    return {
      termId,
      reviewMode,
      sheet: value.sheet,
      blockId: value.blockId,
      draftId: value.sheet === "chat" && typeof value.draftId === "string" && value.draftId
        ? value.draftId
        : null,
    };
  }
  return detailNavigation(termId, reviewMode);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
